import React, { useState, useEffect, useCallback } from 'react';
import MlRiskScoreCard from './MlRiskScoreCard';
import RiskFactorBreakdown from './RiskFactorBreakdown';
import RiskScoreTrendChart from './RiskScoreTrendChart';
import {
  getMlRiskScore,
  getMlRiskScoreHistory,
  getRiskFactorExplanation,
} from '../../../services/mlRiskScore.service';

/**
 * MlRiskScorePanel - Composite panel displaying risk score with factor breakdown and trend
 *
 * This component can be integrated into an applicant's profile view alongside
 * the existing condition-based risk scoring.
 *
 * @param {Object} props
 * @param {number} props.alienId - The alien/subject identifier
 * @param {boolean} [props.showTrend=true] - Whether to show the trend chart
 * @param {boolean} [props.showFactors=true] - Whether to show the factor breakdown
 * @param {boolean} [props.compact=false] - Use compact layout
 */
const MlRiskScorePanel = ({
  alienId,
  showTrend = true,
  showFactors = true,
  compact = false,
}) => {
  const [riskScore, setRiskScore] = useState(null);
  const [factorExplanation, setFactorExplanation] = useState(null);
  const [scoreHistory, setScoreHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all ML risk data
  const fetchRiskData = useCallback(async () => {
    if (!alienId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch risk score (this also calculates a new score)
      const scoreResult = await getMlRiskScore(alienId);
      setRiskScore(scoreResult);

      // Fetch factor explanation if needed
      if (showFactors && scoreResult) {
        const factorsResult = await getRiskFactorExplanation(alienId);
        setFactorExplanation(factorsResult);
      }

      // Fetch history if needed
      if (showTrend) {
        const historyResult = await getMlRiskScoreHistory(alienId, 30);
        setScoreHistory(historyResult);
      }
    } catch (err) {
      console.error('[MlRiskScorePanel] Error fetching risk data:', err);
      setError('Failed to load risk score data');
    } finally {
      setLoading(false);
    }
  }, [alienId, showFactors, showTrend]);

  // Initial load
  useEffect(() => {
    fetchRiskData();
  }, [fetchRiskData]);

  // Handle refresh
  const handleRefresh = () => {
    fetchRiskData();
  };

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Risk Score</h3>
          <button
            onClick={handleRefresh}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4 text-red-500 text-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Compact layout - just the score card
  if (compact) {
    return (
      <MlRiskScoreCard
        riskScore={riskScore}
        loading={loading}
        onRefresh={handleRefresh}
      />
    );
  }

  // Full layout - score card, factors, and trend
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">
          Risk-Driven Assessment
        </h2>
        <div className="text-xs text-gray-500">
          AI-powered behavioral analysis
        </div>
      </div>

      {/* Main content grid */}
      <div className={`grid gap-4 ${showFactors ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Left column: Score card + Trend */}
        <div className="space-y-4">
          <MlRiskScoreCard
            riskScore={riskScore}
            loading={loading}
            onRefresh={handleRefresh}
          />

          {showTrend && (
            <RiskScoreTrendChart history={scoreHistory} loading={loading} />
          )}
        </div>

        {/* Right column: Factor breakdown */}
        {showFactors && (
          <RiskFactorBreakdown
            factorExplanation={factorExplanation}
            showRecommendations={true}
          />
        )}
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-blue-500 text-lg">{'\u2139\uFE0F'}</span>
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">About Risk Scoring</p>
            <p>
              This score is calculated by analyzing behavioral patterns, anomaly frequency,
              compliance history, and routine stability. It supplements the existing
              condition-based scoring system to provide a more comprehensive risk assessment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MlRiskScorePanel;
